import React from 'react';
import { ArrayInput, FormDataConsumer, SimpleFormIterator, minLength } from 'react-admin';
import { Typography, Grid } from '@mui/material';
import { AddCircleOutline } from '@mui/icons-material';
import { ReactComponent as DeleteIcon } from 'src/assets/trash-can.svg';
import { palette } from 'src/Theme';
import { disableFirstRemoveStyle, editContainerStyles, formIteratorStyles, titleFontStyle } from 'src/styles';
import { ShsButton, ShsDatePicker, ShsTextInput } from 'src/components/common';
import { LinkInput } from '../components/LinkInput';
import { useFormContext } from 'react-hook-form';
import { DiplomaType } from '../types';

const employeeFormIteratorStyles = {
    '.RaSimpleFormIterator-line': {
        gap: '16px',
        alignItems: 'baseline'
    },
    '.RaSimpleFormIterator-action': {
        visibility: 'visible',
        cursor: 'pointer'
    }
};

const EmployeeDocumentsForm = () => {
    const { getValues } = useFormContext();
    const values = getValues();

    const isOkDiplomas = () => {
        const diplomas = values?.employeeDetails?.diplomas;
        let isOk = true;
        diplomas?.map((diploma: DiplomaType) => {
            if (!diploma?.diplomaLink || !diploma?.diplomaSupplementLink) {
                isOk = false;
            }
        });
        return isOk;
    };
    const isOkRefresherCertificates = () => {
        const refresherCertificates = values?.employeeDetails?.refresherCertificates;
        let isOk = true;
        refresherCertificates?.map((certificate: { certificateLink: string }) => {
            if (!certificate?.certificateLink) {
                isOk = false;
            }
        });
        return isOk;
    };

    return (
        <Grid sx={editContainerStyles} style={{ background: palette.primary.creamyWhite, padding: '16px 32px' }}>
            <Typography sx={titleFontStyle} variant="h6" component="h6" mb={3}>
                Диплом
            </Typography>
            <ArrayInput
                source="employeeDetails.diplomas"
                label=""
                defaultValue={[{ diplomaLink: '', diplomaSupplementLink: '' }]}
                validate={minLength(1, 'Потрібно додати хоча б один диплом')}>
                <SimpleFormIterator
                    fullWidth
                    disableClear
                    disableReordering
                    sx={{
                        ...disableFirstRemoveStyle,
                        ...formIteratorStyles,
                        ...employeeFormIteratorStyles
                    }}
                    addButton={
                        <ShsButton
                            styleType="text"
                            sx={{ p: 0, pt: 2 }}
                            label="Додати диплом"
                            disabled={!isOkDiplomas()}
                            icon={<AddCircleOutline />}
                        />
                    }
                    removeButton={<DeleteIcon fill={palette.error.main} />}>
                    <FormDataConsumer>
                        {({ getSource }) => (
                            <Grid container columnSpacing={4} rowSpacing={1}>
                                <Grid item xs={12}>
                                    <LinkInput source={getSource('diplomaLink')} label="Диплом" required />
                                    <LinkInput
                                        source={getSource('diplomaSupplementLink')}
                                        label="Додаток до диплому"
                                        required
                                    />
                                </Grid>
                            </Grid>
                        )}
                    </FormDataConsumer>
                </SimpleFormIterator>
            </ArrayInput>

            <Typography sx={titleFontStyle} variant="h6" component="h6" mt={3} mb={3}>
                Підвищення кваліфікації
            </Typography>
            <ArrayInput
                source="employeeDetails.refresherCertificates"
                label=""
                defaultValue={[{ certificateLink: '' }]}>
                <SimpleFormIterator
                    fullWidth
                    disableClear
                    disableReordering
                    sx={{
                        ...formIteratorStyles,
                        ...employeeFormIteratorStyles,
                        '& .RaSimpleFormIterator-list': {
                            borderBottom: 'solid 1px rgba(0, 0, 0, 0.12) !important'
                        },
                        '& .RaSimpleFormIterator-line': {
                            borderBottom: 'none !important'
                        }
                    }}
                    addButton={
                        <ShsButton
                            styleType="text"
                            sx={{ p: 0, pt: 2 }}
                            label="Додати сертифікат"
                            disabled={!isOkRefresherCertificates()}
                            icon={<AddCircleOutline />}
                        />
                    }
                    removeButton={<DeleteIcon fill={palette.error.main} />}>
                    <FormDataConsumer>
                        {({ getSource }) => (
                            <Grid container columnSpacing={4} rowSpacing={1}>
                                <Grid item xs={12}>
                                    <LinkInput
                                        source={getSource('certificateLink')}
                                        label="Сертифікати підвищення кваліфікації"
                                    />
                                </Grid>
                            </Grid>
                        )}
                    </FormDataConsumer>
                </SimpleFormIterator>
            </ArrayInput>

            <Typography sx={titleFontStyle} variant="h6" component="h6" mt={3} mb={3}>
                Додаткові документи
            </Typography>

            <LinkInput source="employeeDetails.employmentHistoryLink" label="Трудова книжка" required />
            <LinkInput source="employeeDetails.medicalHistoryLink" label="Медична книжка" required />

            <ShsDatePicker
                removePaddingTop
                source="employeeDetails.medicalCheckup"
                label="Дата медичного огляду"
                labelPosition="left"
                required
            />

            <LinkInput
                sx={{ mt: '20px' }}
                source="employeeDetails.nameChangeDocumentLink"
                label="Документ про зміну прізвища, імені або по-батькові"
            />
            <LinkInput source="employeeDetails.temporarilyDisplacedDocumentLink" label="Довідка ВПО" />
            <LinkInput source="employeeDetails.attestationLetterLink" label="Атестаційний лист" />

            <ArrayInput source="employeeDetails.additionalDocuments" label="">
                <SimpleFormIterator
                    fullWidth
                    disableClear
                    disableReordering
                    sx={{
                        ...formIteratorStyles,
                        ...employeeFormIteratorStyles,
                        // fixme: form uses several grid input components with different styles, once synced, styles below should be removed
                        '& .RaSimpleFormIterator-action': {
                            marginTop: 'calc(8px + 16px + 10px)' // grid item padding + grid margin + gap between input and label
                        },
                        '& .RaSimpleFormIterator-line': {
                            alignItems: 'center',
                            borderBottom: 'none !important'
                        },
                        '& .RaSimpleFormIterator-list': {
                            borderBottom: 'solid 1px rgba(0, 0, 0, 0.12) !important'
                        }
                    }}
                    addButton={
                        <ShsButton
                            styleType="text"
                            sx={{ p: 0, pt: 2 }}
                            label="Додати інший документ"
                            icon={<AddCircleOutline />}
                        />
                    }
                    removeButton={<DeleteIcon fill={palette.error.main} />}>
                    <FormDataConsumer>
                        {({ getSource }) => (
                            <Grid container columnSpacing={4} rowSpacing={1} alignItems="end">
                                <Grid item xs={6}>
                                    <ShsTextInput
                                        source={getSource('name')}
                                        label="Назва документу"
                                        placeholder="Вкажіть назву документу"
                                        sx={{ width: '100%' }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <ShsTextInput
                                        source={getSource('link')}
                                        placeholder="Вкажіть лінк на файл"
                                        sx={{ width: '100%' }}
                                    />
                                </Grid>
                            </Grid>
                        )}
                    </FormDataConsumer>
                </SimpleFormIterator>
            </ArrayInput>
        </Grid>
    );
};

export default EmployeeDocumentsForm;
